import { Dispatch, FC, useEffect, useState } from "react";
import {
  Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Stack
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import DoneIcon from "@mui/icons-material/Done";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { AxiosInstance } from "axios";
import { AlertType } from "../../../types/AlertType";
import { DataFacture } from "./TableauDeBordFacturation";

interface DialogDateEnvoiFactureProps {
  open: boolean;
  http: AxiosInstance;
  /** Lignes cochées dans le tableau de bord */
  facturesSelectionnees: DataFacture[];
  onClose: () => void;
  /** Appelé après un envoi réussi : vide la sélection et recharge la liste */
  onSuccess: () => void;
  setOpenSnack: Dispatch<React.SetStateAction<boolean>>;
  setAlerteMessage: Dispatch<React.SetStateAction<string>>;
  setAlertType: Dispatch<React.SetStateAction<AlertType>>;
}

/**
 * Pop-up de saisie de la date d'envoi de la facture au client.
 * La date est préremplie à aujourd'hui et reste modifiable.
 */
const DialogDateEnvoiFacture: FC<DialogDateEnvoiFactureProps> = ({
  open,
  http,
  facturesSelectionnees,
  onClose,
  onSuccess,
  setOpenSnack,
  setAlerteMessage,
  setAlertType,
}) => {
  const [dateEnvoi, setDateEnvoi] = useState<Dayjs | null>(dayjs());
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    // Chaque ouverture repart de la date du jour
    if (open) {
      setDateEnvoi(dayjs());
    }
  }, [open]);

  const dateValide: boolean = Boolean(dateEnvoi && dateEnvoi.isValid());

  const handleValider = () => {
    if (!dateValide) {
      return;
    }

    setIsSaving(true);

    const data = {
      dateEnvoi: dateEnvoi?.format("YYYY-MM-DD"),
      // Le back regroupe par période x piv_oper : la devise n'intervient pas.
      factures: facturesSelectionnees.map((facture) => ({
        piv_oper_socifact_codes_id: facture?.piv_oper_socifact_codes_id,
        mois: facture?.periode_mois,
        annee: facture?.periode_annee,
      })),
    };

    http
      .post("facturation/dashboard/marquer-envoyee", data)
      .then((res) => {
        const { validate, message } = res?.data ?? {};

        setAlerteMessage(message ?? "");
        setAlertType(validate ? "success" : "warning");
        setOpenSnack(true);

        if (validate) {
          onSuccess();
        }

        onClose();
      })
      .catch((error) => {
        console.log(error);
        setAlerteMessage("Désolé! Une erreur est survenue !");
        setAlertType("error");
        setOpenSnack(true);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const nbFactures: number = facturesSelectionnees.length;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Facture envoyée au client</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {nbFactures > 1
            ? `Saisissez la date d'envoi des ${nbFactures} factures sélectionnées.`
            : "Saisissez la date d'envoi de la facture sélectionnée."}
        </DialogContentText>
        <Stack sx={{ mt: 1 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label="Date d'envoi de la facture"
              format="DD/MM/YYYY"
              value={dateEnvoi}
              onChange={(nouvelleDate) => setDateEnvoi(nouvelleDate)}
              slotProps={{
                textField: {
                  size: "small",
                  color: "warning",
                  fullWidth: true,
                  error: !dateValide,
                  helperText: !dateValide ? "Date invalide" : "",
                },
              }}
            />
          </LocalizationProvider>
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          onClick={() => handleValider()}
          endIcon={<DoneIcon />}
          loading={isSaving}
          loadingPosition="end"
          disabled={!dateValide || nbFactures === 0}
          sx={{ textTransform: "none" }}
          variant="contained"
          color="warning"
        >
          Valider
        </LoadingButton>
        <Button
          onClick={() => onClose()}
          variant="contained"
          color="inherit"
          sx={{ textTransform: "none" }}
          startIcon={<ExitToAppIcon />}
        >
          Annuler
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DialogDateEnvoiFacture;
